import { useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/app/components/ui/dialog';
import { Button } from '@/app/components/ui/button';
import { Download, Printer } from 'lucide-react';

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

/**
 * Markup for the print window. It carries no inline <script>: the parent
 * window drives printing once the QR image has loaded (see
 * printAfterImagesLoad), so React never sees a script tag.
 */
export function buildPrintDocument(title: string, bodyHtml: string): string {
  return `<!DOCTYPE html>
<html>
  <head>
    <title>QR Code - ${escapeHtml(title)}</title>
    <style>
      body { display: flex; flex-direction: column; align-items: center; justify-content: center; min-height: 100vh; margin: 0; font-family: system-ui, sans-serif; }
      img { width: 300px; height: 300px; }
      h2 { margin: 16px 0 4px; font-size: 20px; }
      p { margin: 0; color: #666; font-size: 14px; }
    </style>
  </head>
  <body>
    ${bodyHtml}
  </body>
</html>`;
}

/** Print the window from the opener once every image in it has loaded, then close it. */
export function printAfterImagesLoad(printWindow: Window): void {
  // Snapshot the not-yet-complete images once so an image that finishes
  // between the check and listener registration can't be skipped.
  const pendingImages = Array.from(printWindow.document.images).filter((img) => !img.complete);
  let pending = pendingImages.length;
  let printed = false;

  const print = () => {
    if (printed) return;
    printed = true;
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  if (pending === 0) {
    print();
    return;
  }
  const onSettled = () => {
    pending -= 1;
    if (pending === 0) print();
  };
  pendingImages.forEach((img) => {
    img.addEventListener('load', onSettled, { once: true });
    img.addEventListener('error', onSettled, { once: true });
  });
}

interface QRCodeModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  qrDataUrl: string | undefined;
  title: string;
  subtitle?: string;
  isLoading?: boolean;
}

export function QRCodeModal({
  open,
  onOpenChange,
  qrDataUrl,
  title,
  subtitle,
  isLoading,
}: QRCodeModalProps) {
  const printRef = useRef<HTMLDivElement>(null);

  const handleDownload = () => {
    if (!qrDataUrl) return;
    const link = document.createElement('a');
    link.href = qrDataUrl;
    link.download = `qr-${title.replace(/\s+/g, '-').toLowerCase()}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handlePrint = () => {
    if (!printRef.current) return;
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    printWindow.document.write(buildPrintDocument(title, printRef.current.innerHTML));
    printWindow.document.close();
    printAfterImagesLoad(printWindow);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>QR Code</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col items-center gap-4 py-4">
          {isLoading ? (
            <div className="w-64 h-64 bg-gray-100 animate-pulse rounded-md" />
          ) : qrDataUrl ? (
            <div ref={printRef} className="flex flex-col items-center">
              <img
                src={qrDataUrl}
                alt={`QR Code for ${title}`}
                className="w-64 h-64"
              />
              <h2 className="mt-3 text-lg font-semibold text-center">{title}</h2>
              {subtitle && (
                <p className="text-sm text-gray-500 text-center">{subtitle}</p>
              )}
            </div>
          ) : (
            <p className="text-sm text-gray-500">Failed to load QR code.</p>
          )}
          <div className="flex gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownload}
              disabled={!qrDataUrl}
            >
              <Download className="h-4 w-4 mr-1" />
              Download
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handlePrint}
              disabled={!qrDataUrl}
            >
              <Printer className="h-4 w-4 mr-1" />
              Print
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
